/**
 * Government fee on a direct invoice line (BE-3d) — the typing centre's payment of the fee and its VATP013 evidence.
 *
 * Only while the invoice is DRAFT; a FINAL invoice shows the payment read-only. A customer-card payment sets the
 * line's fee to 0 (not billed) and needs no evidence. TC money needs evidence — the authorisation plus the uploaded
 * receipt — before the invoice can finalise; it is asked right in the payment form and on the paid fee, the same
 * section as the work order's. Saves that run several calls cover the dialog until every call is back.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Paper,
  Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  Payment as PayIcon, Undo as ReverseIcon, SwapHoriz as ReplaceIcon,
} from '@mui/icons-material';
import { StatusChip, JournalEntryLink, DisbursementEvidenceFields, SavingOverlay } from '../components/accounting';
import { evidenceChanged, OptionalDisbursement } from '../components/accounting/DisbursementEvidenceFields';
import PaymentSourceFields, {
  emptyPayForm, usePaymentLookups, buildPaymentBody, paymentSourceText, missingPayee, defaultPaidFrom, useServiceAccountId,
} from '../components/accounting/PaymentSourceFields';
import { formatCurrency, formatDate } from '../utils/accounting/formatters';
import { invoiceMoneyService } from '../services/accounting/invoiceMoney.service';
import { serverMessage } from '../utils/apiConvert';

// A direct (non work-order, non-Thaklis) line with a government fee the TC pays.
export const isDirectFeeLine = (item) => item.is_external && !item.work_order_line_item_id
  && Number(item.govt_charge_input ?? item.govt_charge ?? 0) > 0;

const InvoiceFeeDialog = ({ open, onClose, invoice, line, payment, editable, canFinance, onChanged }) => {
  const [form, setForm] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const lookups = usePaymentLookups(open, invoice?.internal_organization_id);

  const serviceAccountId = useServiceAccountId(open, line?.internal_service_id);

  useEffect(() => {
    if (open) { setForm(null); setEvidence(null); setError(null); setNotice(null); }
  }, [open]);

  // Only fills an empty "Paid from" when a payment is being recorded; a choice the user made is kept.
  useEffect(() => {
    if (form?.mode !== 'record' || form.from) return;
    const from = defaultPaidFrom(lookups.accounts, serviceAccountId);
    if (from) setForm((f) => (f && !f.from ? { ...f, from } : f));
  }, [form?.mode, form?.from, lookups.accounts, serviceAccountId]);

  if (!line) return null;

  const lineFee = Number(line.govt_charge_input ?? line.govt_charge ?? 0) * Number(line.quantity || 1);
  // a customer-card payment zeroes the billed fee: the amount paid is on the payment
  const fee = lineFee > 0 ? lineFee : Number(payment?.amount || 0);
  const tcMoney = payment && payment.source !== 'CUSTOMER_CARD';
  const saved = { authType: line.disbursement_auth_type, receiptRef: line.disbursement_receipt_ref };
  const evidenceDraft = evidence || { authType: saved.authType || '', receiptRef: saved.receiptRef || '' };
  const setEvidenceDraft = (patch) => setEvidence((e) => ({ ...(e || evidenceDraft), ...patch }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const recordingTcMoney = form?.mode === 'record' && form.from && !form.from.startsWith('CUSTCARD:');

  // The authorisation (when it changed) and then the receipt file.
  const sendEvidence = async () => {
    const id = invoice.id;
    const ref = (evidenceDraft.receiptRef || '').trim();
    if (evidenceDraft.authType && (evidenceDraft.authType !== (saved.authType || '') || ref !== (saved.receiptRef || ''))) {
      await invoiceMoneyService.setFeeEvidence(id, line.id, { authType: evidenceDraft.authType, receiptRef: ref });
    }
    if (evidenceDraft.file) await invoiceMoneyService.uploadFeeReceipt(id, line.id, evidenceDraft.file);
  };

  const run = async (fn, message) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await fn();
      setForm(null);
      setEvidence(null);
      setNotice(message);
      await onChanged();
    } catch (err) {
      setError(serverMessage(err, 'The change failed'));
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    const id = invoice.id;
    if (form.mode === 'record') {
      return run(async () => {
        await invoiceMoneyService.recordFee(id, line.id, buildPaymentBody(form, lookups));
        if (!recordingTcMoney || !evidenceChanged(evidence, saved)) return;
        setForm(null); // the fee is paid: a failed evidence call is retried from the paid fee, not by paying again
        try {
          await sendEvidence();
        } catch (err) {
          throw new Error(`The fee is paid, but the evidence was not saved: ${serverMessage(err, 'failed')}`);
        }
      }, 'Government fee paid');
    }
    if (form.mode === 'replace') return run(() => invoiceMoneyService.replaceFee(id, payment.id, form.reason.trim(), buildPaymentBody(form, lookups)), 'Payment replaced');
    return run(() => invoiceMoneyService.reverseFee(id, payment.id, form.reason.trim()), 'Payment reversed');
  };

  const valid = form && (
    form.mode === 'record' ? form.from && form.paidAt && !missingPayee(form)
      : form.mode === 'replace' ? form.from && form.paidAt && !missingPayee(form) && form.reason.trim()
        : form.reason.trim());

  // optional: hidden behind "+ Add disbursement" until added; only started evidence holds back the finalisation
  const evidenceSection = (
    <OptionalDisbursement saved={{ ...saved, hasReceipt: line.disbursement_has_receipt }} disabled={!editable || busy}
      onDiscard={() => setEvidence(null)}
      onRemove={() => run(() => invoiceMoneyService.removeFeeEvidence(invoice.id, line.id), 'Disbursement removed')}>
      <DisbursementEvidenceFields draft={evidenceDraft} set={setEvidenceDraft} status={line.disbursement_review_status}
        hasReceipt={Boolean(line.disbursement_has_receipt)} disabled={!editable || busy} onError={setError}
        fetchUrl={line.disbursement_has_receipt ? () => invoiceMoneyService.feeReceiptUrl(invoice.id, line.id) : null} />
    </OptionalDisbursement>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} disableEscapeKeyDown={busy} maxWidth="sm" fullWidth
      PaperProps={{ sx: { position: 'relative' } }}>
      <SavingOverlay open={busy} />
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" variant="h6" fontWeight={600} display="block">Government Fee</Typography>
        <Typography component="span" variant="body2" color="text.secondary" display="block">
          {line.service_name || line.external_service_name}{line.customer_name ? ` - ${line.customer_name}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {!editable && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fee payments change only while the invoice is a draft. Return it to draft to change this payment.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box>
              <Typography fontWeight={600}>{formatCurrency(fee)}</Typography>
              {payment ? (
                <>
                  <StatusChip tone="success" label="Paid" />
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {paymentSourceText(payment, lookups)}{payment.paidToName ? ` → ${payment.paidToName}` : ''} · {formatDate(payment.paidAt)}
                    {payment.receiptRef ? ` · ${payment.receiptRef}` : ''}
                  </Typography>
                  {!tcMoney && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Paid by the customer&apos;s card — the fee is not billed on this invoice.
                    </Typography>
                  )}
                </>
              ) : <StatusChip tone="warning" label="Not paid" />}
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              {payment && <JournalEntryLink id={payment.journalEntryId} />}
              {editable && !form && !payment && (
                <Button size="small" variant="contained" startIcon={<PayIcon />} onClick={() => setForm({ mode: 'record', ...emptyPayForm() })}>
                  Pay government fee
                </Button>
              )}
              {editable && !form && payment && canFinance && (
                <>
                  <Tooltip title="Replace payment"><IconButton size="small" onClick={() => setForm({ mode: 'replace', ...emptyPayForm() })}><ReplaceIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Reverse payment"><IconButton size="small" color="error" onClick={() => setForm({ mode: 'reverse', reason: '' })}><ReverseIcon fontSize="small" /></IconButton></Tooltip>
                </>
              )}
            </Box>
          </Box>

          {/* Disbursement (VATP013) for TC money that paid the fee */}
          {tcMoney && !form && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Disbursement</Typography>
              {evidenceSection}
              {editable && evidenceChanged(evidence, saved) && (
                <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
                  <Button onClick={() => setEvidence(null)} disabled={busy}>Undo</Button>
                  <Button variant="contained" disabled={busy}
                    onClick={() => run(sendEvidence, evidenceDraft.authType === 'VERBAL' && evidenceDraft.file ? 'Evidence sent to finance for review' : 'Evidence saved')}>
                    Save evidence
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>

        {form && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography fontWeight={600} color="primary.main" mb={1.5}>
              {{ record: 'Pay government fee', replace: 'Replace payment', reverse: 'Reverse payment' }[form.mode]}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {(form.mode === 'record' || form.mode === 'replace') && (
                <PaymentSourceFields form={form} set={set} lookups={lookups} amount={fee} />
              )}
              {(form.mode === 'replace' || form.mode === 'reverse') && (
                <TextField size="small" label="Reason *" fullWidth value={form.reason} onChange={(e) => set({ reason: e.target.value })} />
              )}
              {recordingTcMoney && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" fontWeight={600}>Disbursement</Typography>
                  {evidenceSection}
                </>
              )}
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button onClick={() => setForm(null)} disabled={busy}>Back</Button>
              <Button variant="contained" color={form.mode === 'reverse' ? 'error' : 'primary'} disabled={busy || !valid} onClick={submit}>
                {{ record: 'Record', replace: 'Replace', reverse: 'Reverse' }[form.mode]}
              </Button>
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceFeeDialog;
